/**
 * Resolve a CSS custom property (design token) to its current computed value.
 *
 * Used by chart/canvas code that builds colors at RUNTIME (inside methods invoked
 * in the browser), where reading the active theme's CSS variables is the correct
 * way to stay in sync with `src/app/sass/_tokens.scss`. SSR-safe: when there is no
 * DOM (e.g. server-side prerender), it returns the provided fallback instead of
 * throwing.
 *
 * @param name     the custom-property name, e.g. '--color-anomaly-red'
 * @param fallback value to return when the DOM/getComputedStyle is unavailable
 * @returns        the trimmed token value, or `fallback` if it can't be resolved
 */
export function cssToken(name: string, fallback = ''): string {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!value && !fallback && typeof ngDevMode !== 'undefined' && ngDevMode) {
    // Mistyped/undefined token names otherwise fail silently as invalid colors.
    console.warn(`cssToken: '${name}' resolved to an empty value (not defined in _tokens.scss?)`);
  }
  return value || fallback;
}

declare const ngDevMode: boolean | undefined;
